import { Field, Formik, Form } from 'formik';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/tailwindui/fieldset';
import { Field as FieldComponent } from '@/src/tailwindui/fieldset';
import { Textarea } from '@/src/tailwindui/textarea';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import useCrowdGallery from './hooks/useCrowdGallery';
import type { CrowdGallerySettings as CrowdGallerySettingsType } from './hooks/useCrowdGallery';

interface CrowdGallerySettingsProps {
    galleryId: number;
}

export default function CrowdGallerySettings({
    galleryId,
}: CrowdGallerySettingsProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState<CrowdGallerySettingsType | null>(
        null,
    );
    const { getCrowdGallery, saveCrowdGallery } = useCrowdGallery();

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const settings: CrowdGallerySettingsType =
                    await getCrowdGallery(galleryId);
                if (isMounted) {
                    setSettings(settings);
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg">Loading crowd gallery settings...</div>
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg text-red-600">
                    Failed to load crowd gallery settings
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Crowd Gallery Settings
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Configure SMS-based gallery sharing and crowd photo
                    collection.
                </p>
            </div>

            <Formik
                initialValues={settings}
                onSubmit={async (values, actions) => {
                    const wasSuccessful = await saveCrowdGallery(
                        settings.id,
                        values,
                    );
                    actions.setSubmitting(false);

                    if (wasSuccessful) {
                        toast.success(
                            'Crowd gallery settings saved successfully',
                        );
                    } else {
                        toast.error('Failed to save crowd gallery settings');
                    }
                }}
            >
                {(props) => (
                    <Form
                        className="space-y-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        {/* Basic Settings */}
                        <div className="space-y-4">
                            <div>
                                <h4 className="text-md font-medium text-gray-900 dark:text-white">
                                    Basic Settings
                                </h4>
                            </div>

                            <FieldComponent>
                                <div className="flex items-center space-x-2">
                                    <Field
                                        id="enabled"
                                        name="enabled"
                                        type="checkbox"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <Label
                                        htmlFor="enabled"
                                        className="text-sm font-medium"
                                    >
                                        Enable crowd gallery
                                    </Label>
                                </div>
                            </FieldComponent>

                            <FieldComponent>
                                <Label htmlFor="keyword">Short Code</Label>
                                <Field
                                    as={Input}
                                    id="keyword"
                                    name="keyword"
                                    placeholder="Enter short code"
                                />
                            </FieldComponent>

                            <FieldComponent>
                                <Label htmlFor="welcomeMessage">
                                    Welcome Message
                                </Label>
                                <Field
                                    as={Textarea}
                                    id="welcomeMessage"
                                    name="welcomeMessage"
                                    rows={4}
                                    placeholder="Hey you, Smilebooth here! Text your photos to be added to the gallery. Txt and data rates may apply."
                                />
                            </FieldComponent>
                        </div>

                        {/* Response Settings */}
                        <div className="space-y-4">
                            <div>
                                <h4 className="text-md font-medium text-gray-900 dark:text-white">
                                    Response Settings
                                </h4>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Configure what users receive when they text
                                    photos.
                                </p>
                            </div>

                            <FieldComponent>
                                <Label htmlFor="responseMessage">
                                    Response Message
                                </Label>
                                <Field
                                    as={Textarea}
                                    id="responseMessage"
                                    name="responseMessage"
                                    rows={4}
                                    placeholder="Enter response message sent to users"
                                />
                            </FieldComponent>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                <FieldComponent>
                                    <div className="flex items-center space-x-2">
                                        <Field
                                            id="sendPhoto"
                                            name="sendPhoto"
                                            type="checkbox"
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <Label
                                            htmlFor="sendPhoto"
                                            className="text-sm font-medium"
                                        >
                                            Send photo back
                                        </Label>
                                    </div>
                                </FieldComponent>

                                <FieldComponent>
                                    <div className="flex items-center space-x-2">
                                        <Field
                                            id="sendPersonalGalleryLink"
                                            name="sendPersonalGalleryLink"
                                            type="checkbox"
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <Label
                                            htmlFor="sendPersonalGalleryLink"
                                            className="text-sm font-medium"
                                        >
                                            Send personal gallery link
                                        </Label>
                                    </div>
                                </FieldComponent>

                                <FieldComponent>
                                    <div className="flex items-center space-x-2">
                                        <Field
                                            id="sendGalleryLink"
                                            name="sendGalleryLink"
                                            type="checkbox"
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <Label
                                            htmlFor="sendGalleryLink"
                                            className="text-sm font-medium"
                                        >
                                            Send gallery link
                                        </Label>
                                    </div>
                                </FieldComponent>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <Button
                                type="submit"
                                disabled={props.isSubmitting}
                                className="bg-black hover:bg-gray-800 text-white"
                            >
                                {props.isSubmitting
                                    ? 'Saving...'
                                    : 'Save Settings'}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}
